/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { redirect } from 'react-router';
import type { Route } from './+types/_app.$contentId[.html]';
import { fetchContentAssetHtml, type ContentAsset } from '@/lib/api/content-asset.server';
import { fetchProductById } from '@/lib/api/products.server';
import { getCanonicalProductPath } from '@/lib/product/canonical-product-url';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getPublicOrigin } from '@/utils/schema-url';
import { getLogger } from '@/lib/logger.server';
import { OurStoryPage } from '@/components/content-pages/our-story-page';
import { loadOurStoryData, type OurStoryPageData } from '@/components/content-pages/our-story-page.data';
import { MaterialsPage } from '@/components/content-pages/materials-page';

/**
 * RGBARRY-CUSTOMIZATION: single-segment content route at `/:contentId.html` (W-23754187).
 *
 * Production baggallini.com (SFRA) serves content assets at a BARE, single-segment `.html`
 * URL — `/customer-service.html` — while products always carry a decorative slug, giving a
 * TWO-segment `.html` URL — `/modern-everywhere-bagg/EWH710-BG-B1229-NS.html`. Per the
 * cc-baggallini-internal-implementation thread (Charles / Luke, 2026-08-12) we drop the
 * earlier `/c/` prefix and disambiguate content from product purely by URL SHAPE:
 *
 *   - 1 path segment + `.html`  →  CONTENT  (this route)
 *   - 2 path segments + `.html` →  PRODUCT  (`_app.$slug.$productId[.html]`)
 *
 * Categories never end in `.html`, so they remain unambiguous and flow to the catch-all.
 * Dropping the prefix means SF Next URLs are byte-identical to production's and no `/c/`
 * migration redirect is needed — the win Luke called out ("urls all same as prod").
 *
 * LOAD-BEARING ASSUMPTION (baggallini-specific — verify per-site before reuse). Resolving a
 * single-segment `.html` as content BY CONVENTION (strip `.html`, the remaining segment IS the
 * content-asset id, fetch it directly with NO `getUrlMapping` lookup) is only correct because
 * baggallini's content-asset URL slugs EQUAL their content-asset ids: two independent audits
 * (Charles: BM SEO settings; Luke: BM + storefront URL export) confirmed baggallini applied ZERO
 * SEO/Page-URL overrides to content assets — they serve at `/{contentAssetId}.html` verbatim
 * (`/customer-service.html` → asset id `customer-service`). Products and categories ARE heavily
 * aliased (slug ≠ id), which is why THEY always go through `getUrlMapping` (see `_app.$`). Luke
 * also audited the category tree: no baggallini category ends in `.html`, so the single-segment
 * `.html` space is exclusively content here — no category can collide with this route.
 *   REVISIT if a future site (or baggallini later) adds content SEO aliases so a content `.html`
 *   slug no longer equals its asset id: this route would then 404 a real indexed URL and would
 *   need a `getUrlMapping` fallback before the 404. That is NOT a general Storefront Next truth —
 *   it is a documented baggallini engagement assumption (design thread, Charles/Luke, 2026-08-12).
 *
 * CONTENT-FIRST, PRODUCT-FALLBACK. A single-segment `.html` is treated as content by default
 * (Charles: "that would always be a content url under this assumption"). But production ALSO
 * 301-redirects a bare `/{PRODUCT_ID}.html` (no slug) up to the canonical slugged PDP —
 * `/EWH710-BG-B1229-NS.html` → `/modern-everywhere-bagg/EWH710-BG-B1229-NS.html` — so that the
 * same product isn't indexable at two URLs (a duplicate-content SEO loss). We reproduce both
 * without a prefix by resolving in order:
 *
 *   1. Try the content asset by id. If it exists, render it (the common case).
 *   2. Otherwise fall back to a product lookup by the same id. If it resolves AND a canonical
 *      slug can be derived, 301 to the 2-segment canonical `/{slug}/{id}.html` (which matches
 *      the dedicated product route and renders — no loop).
 *   3. Otherwise 404.
 *
 * Content wins ties, so this only reaches the product fallback when NO content asset owns the
 * id — the collision case (a content slug that equals a product id) is vanishingly unlikely
 * because product ids are not SEO-friendly slugs. The `.html` suffix keeps the URL aligned with
 * Business-Manager sitemap generation (BM emits `.html` for content assets).
 */
/**
 * RGBARRY-CUSTOMIZATION (W-23754187): custom React content pages served at `.html`.
 *
 * A few marketing pages (Our Story, Our Materials) are hand-built React pages rather than
 * Business-Manager content assets, but marketing wants them at the same single-segment `.html`
 * URLs as everything else (`/our-story.html`, `/materials.html`). Because THIS route owns the
 * entire `/:contentId.html` shape in React Router's ranking, a literal `_app.our-story[.html].tsx`
 * route can't reliably win the tie — so we special-case those ids HERE, before the content-asset
 * fetch, and render the React component instead. Everything else falls through to the OOTB
 * content-asset → product-fallback → 404 flow below. Page bodies live in
 * `@/components/content-pages/*` (kept out of `src/routes/` so no dead route file lingers;
 * `generate-cartridge` still discovers their `@PageType` metadata since it scans all of `src/`).
 */
const CUSTOM_CONTENT_PAGES = new Set(['our-story', 'materials']);

export type ContentAssetPageData = {
    kind: 'content';
    asset: ContentAsset;
    canonicalUrl: string;
};

export type ContentRouteData =
    | ContentAssetPageData
    | { kind: 'our-story'; data: OurStoryPageData }
    | { kind: 'materials' };

export async function loader(args: Route.LoaderArgs): Promise<ContentRouteData> {
    const logger = getLogger(args.context);
    const { contentId } = args.params;
    logger.debug('ContentAssetRoute: loader starting', { contentId });

    // 0) RGBARRY-CUSTOMIZATION (W-23754187): short-circuit custom React pages BEFORE any
    //    content-asset fetch, so `/our-story.html` and `/materials.html` render their React
    //    components rather than resolving as (non-existent) BM content assets.
    if (CUSTOM_CONTENT_PAGES.has(contentId)) {
        if (contentId === 'our-story') {
            return { kind: 'our-story', data: await loadOurStoryData(args) };
        }
        return { kind: 'materials' };
    }

    // 1) Content-first: a single-segment `.html` is a content URL by default.
    const asset = await fetchContentAssetHtml(args.context, contentId);
    if (asset) {
        // Self-referencing canonical from the live request path. Feeds `SeoMeta`'s `og:url`; the
        // real `rel="canonical"` is emitted centrally by `root.tsx` from the same pathname, so the
        // two stay consistent. Derive the origin from the PUBLIC forwarding headers via
        // `getPublicOrigin` — NOT `url.origin` — because on Managed Runtime `request.url` carries
        // the internal Lambda/CDN origin, which would leak into the emitted `og:url`.
        const url = new URL(args.request.url);
        const canonicalUrl = buildCanonicalUrl(getPublicOrigin(args.request), url.pathname, url.search);
        return { kind: 'content', asset, canonicalUrl };
    }

    // 2) Product fallback: no content asset owns this id, so this may be a bare
    //    `/{PRODUCT_ID}.html` (no decorative slug). Mirror SFRA and 301 up to the canonical
    //    2-segment PDP so the product isn't reachable at two URLs. A product-fetch failure
    //    (including 404) means "not a product either" — fall through to the 404 below rather
    //    than surface a 500.
    let product: Awaited<ReturnType<typeof fetchProductById>> = null;
    try {
        product = await fetchProductById(args.context, contentId);
    } catch {
        product = null;
    }
    const canonicalPath = product && getCanonicalProductPath(product.slugUrl, contentId);
    if (canonicalPath) {
        const url = new URL(args.request.url);
        logger.debug('ContentAssetRoute: bare product id, 301 to canonical PDP', {
            from: url.pathname,
            to: canonicalPath,
        });
        // 301 permanent; preserve the query string. Target is a 2-segment `/{slug}/{id}.html`
        // that matches the dedicated product route (not this one), so it renders — no loop.
        throw redirect(`${canonicalPath}${url.search}`, 301);
    }

    // 3) Neither content nor product — a real 404.
    logger.warn('ContentAssetRoute: no content asset or product for id, returning 404', { contentId });
    throw new Response('Not Found', { status: 404 });
}

// RGBARRY-CUSTOMIZATION: classify this route for the GTM data layer's page-view event
// (see src/analytics/route-analytics.ts).
export const handle = {
    analytics: { routeType: 'content' },
};

export default function ContentAssetRoute({ loaderData }: { loaderData: ContentRouteData }) {
    // RGBARRY-CUSTOMIZATION (W-23754187): branch on the custom React pages before the OOTB
    // content-asset render.
    if (loaderData.kind === 'our-story') {
        return <OurStoryPage loaderData={loaderData.data} />;
    }
    if (loaderData.kind === 'materials') {
        return <MaterialsPage />;
    }

    return (
        <>
            {/* Per-page SEO metadata so a migrated content page keeps its title, description,
                and a self-referencing canonical. */}
            <SeoMeta
                title={loaderData.asset.title}
                description={loaderData.asset.description}
                openGraph={{ type: 'article', url: loaderData.canonicalUrl }}
            />
            <div className="section-container py-12 lg:py-16">
                <div
                    className="content-asset-body"
                    // oxlint-disable-next-line react/no-danger -- content asset HTML is sanitized in fetchContentAssetHtml
                    dangerouslySetInnerHTML={{ __html: loaderData.asset.html }}
                />
            </div>
        </>
    );
}
